const TWOD_POLL_HIDDEN_MIN_DELAY_MS = 10000;

const waitForVisibilityAwareDelay = (visibleDelayMs, hiddenDelayMs = TWOD_POLL_HIDDEN_MIN_DELAY_MS) =>
  new Promise((resolve) => {
    const hasDocument = typeof document !== 'undefined';
    const isHidden = hasDocument && document.visibilityState === 'hidden';
    const effectiveDelayMs = isHidden ? Math.max(visibleDelayMs, hiddenDelayMs) : visibleDelayMs;
    let timeoutId = null;

    function cleanup() {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (hasDocument && isHidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    }

    function complete() {
      cleanup();
      resolve();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        complete();
      }
    }

    if (hasDocument && isHidden) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    timeoutId = window.setTimeout(complete, effectiveDelayMs);
  });

export const normalizeJobStatus = (value) => {
  if (!value) {
    return '';
  }
  const raw = typeof value === 'string' ? value : String(value);
  return raw.toLowerCase();
};

export const pollJobUntilCompleted = async (jobId, fetchStatus, updateState, options = {}) => {
  const startedAt = Date.now();
  const maxWaitMs = Number.isFinite(options.maxWaitMs) ? options.maxWaitMs : 180000;
  const baseDelayMs = Number.isFinite(options.delayMs) ? options.delayMs : 1500;
  let attempts = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - startedAt > maxWaitMs) {
      updateState((prev) =>
        prev
          ? {
              ...prev,
              status: 'failed',
              message: 'Timeout',
            }
          : prev
      );
      return 'failed';
    }

    let statusPayload;
    try {
      // eslint-disable-next-line no-await-in-loop
      statusPayload = await fetchStatus(jobId);
    } catch (error) {
      updateState((prev) => (prev ? { ...prev, status: 'failed', message: 'Status check failed' } : prev));
      return 'failed';
    }

    const status = normalizeJobStatus(statusPayload?.status);
    const progressRaw = statusPayload?.progress_percentage ?? statusPayload?.progressPercentage;
    const progress = Number.isFinite(progressRaw) ? progressRaw : Number(progressRaw);
    const progressMessage =
      Number.isFinite(progress) && status !== 'completed' && status !== 'failed' && status !== 'cancelled'
        ? `${Math.round(progress)}%`
        : null;

    updateState((prev) =>
      prev
        ? {
            ...prev,
            status,
            message: progressMessage || prev.message,
          }
        : prev
    );

    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      return status;
    }

    attempts += 1;
    const retryDelayMs = attempts < 20 ? baseDelayMs : attempts < 80 ? 2500 : 5000;
    // eslint-disable-next-line no-await-in-loop
    await waitForVisibilityAwareDelay(retryDelayMs);
  }
};
